import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/ViewDiscounts.css';

interface Discount {
  discountId: string;
  discount: number;
  product?: string;
  allProducts?: boolean;
}

interface UserWithDiscounts {
  userId: string;
  userName: string;
  userEmail: string;
  discounts: Discount[];
}

const ViewDiscounts: React.FC = () => {
  const { token } = useAuth();
  const [usersWithDiscounts, setUsersWithDiscounts] = useState<UserWithDiscounts[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithDiscounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteStatus, setDeleteStatus] = useState<{ id: string, status: 'deleting' | 'success' | 'error' } | null>(null);
  
  // Search and sort states
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | ''>('');

  // Check if any filter is active
  const isFilterActive = searchQuery !== '' || sortOrder !== '';

  // Fetch all users with their discounts
  useEffect(() => {
    const fetchDiscounts = async () => {
      if (!token) return;
      
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/discounts/users`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }
        
        const data = await response.json();
        
        // For demo purposes, add email if not provided by API
        const processedData = data.map((user: UserWithDiscounts) => ({
          ...user,
          email: user.userEmail
        }));
        console.log(processedData);
        
        setUsersWithDiscounts(processedData);
        setFilteredUsers(processedData);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch discounts:', err);
        setError('Failed to load discounts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDiscounts();
  }, [token]);

  // Filter and sort users based on search query and sort order
  useEffect(() => {
    if (!usersWithDiscounts.length) return;

    let result = [...usersWithDiscounts];
    
    // Apply search filter (name or email)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(user => 
        user.userName.toLowerCase().includes(query) || 
        (user.userEmail && user.userEmail.toLowerCase().includes(query))
      );
    }
    
    // Apply sorting
    if (sortOrder) {
      result.sort((a, b) => {
        if (sortOrder === 'asc') {
          return a.discounts.length - b.discounts.length;
        } else {
          return b.discounts.length - a.discounts.length;
        }
      });
    }
    
    setFilteredUsers(result);
  }, [usersWithDiscounts, searchQuery, sortOrder]);

  // Toggle sort order
  const toggleSortOrder = () => {
    if (sortOrder === '') {
      setSortOrder('asc');
    } else if (sortOrder === 'asc') {
      setSortOrder('desc');
    } else {
      setSortOrder('');
    }
  };

  // Clear all search fields
  const clearSearchFields = () => {
    setSearchQuery('');
    setSortOrder('');
  };

  // Handle delete discount
  const handleDeleteDiscount = async (discountId: string) => {
    if (!token) return;
    
    try {
      setDeleteStatus({ id: discountId, status: 'deleting' });
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/discounts/${discountId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      
      setDeleteStatus({ id: discountId, status: 'success' });
      
      // Update the state to remove the deleted discount
      setUsersWithDiscounts(prevUsers => {
        return prevUsers.map(user => ({
          ...user,
          discounts: user.discounts.filter(discount => discount.discountId !== discountId)
        })).filter(user => user.discounts.length > 0); // Remove users with no discounts
      });
      
      // Clear the delete status after a delay
      setTimeout(() => {
        setDeleteStatus(null);
      }, 2000);
      
    } catch (err) {
      console.error('Failed to delete discount:', err);
      setDeleteStatus({ id: discountId, status: 'error' });
      
      // Clear the error status after a delay
      setTimeout(() => {
        setDeleteStatus(null);
      }, 3000);
    }
  };

  if (loading && filteredUsers.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading">Loading discounts...</div>
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (usersWithDiscounts.length === 0) {
    return <div className="no-discounts">
      <span className="no-data-icon">📊</span>
      <h3>No Discounts Found</h3>
      <p>There are currently no discounts in the system. Add some discounts to see them here.</p>
    </div>;
  }

  return (
    <div className="view-discounts-container">
      <div className="discounts-header">
        <div className="header-row">
          <h1 className="page-title">Active Discounts</h1>
          
          <div className="search-controls-wrapper">
            <div className="search-controls">
              <div className="search-field">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by name or email"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <span className="search-icon">🔍</span>
              </div>
              
              <div 
                className={`sort-filter ${sortOrder ? `sort-${sortOrder}` : ''}`}
                onClick={toggleSortOrder}
              >
                <div className="sort-display">
                  {sortOrder === '' && 'Sort by discounts'}
                  {sortOrder === 'asc' && 'Fewest first'}
                  {sortOrder === 'desc' && 'Most first'}
                  <span className="sort-icon">
                    {sortOrder === 'asc' && '↑'}
                    {sortOrder === 'desc' && '↓'}
                    {sortOrder === '' && '⇅'}
                  </span>
                </div>
              </div>
            </div>
            
            {isFilterActive && (
              <button 
                className="clear-search-button"
                onClick={clearSearchFields}
                title="Clear all filters"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>
      
      <div className="discounts-list">
        {loading ? (
          <div className="loading-message">Loading discounts...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : filteredUsers.length === 0 ? (
          <div className="no-results">
            <p>No users found matching your search criteria.</p>
          </div>
        ) : (
          filteredUsers.map(user => (
            <div key={user.userId} className="user-discounts-card">
              <div className="card-header">
                <div className="user-avatar" style={{ backgroundColor: '#4f46e5' }}>{user.userName.charAt(0).toUpperCase()}</div>
                <div className="user-info">
                  <h2 className="user-name" style={{ color: '#ffffff' }}>{user.userName}</h2>
                  <div className="user-email">{user.userEmail}</div>
                </div>
              </div>
              
              <div className="discounts-list-items">
                {user.discounts.map(discount => {
                  return (
                    <div key={discount.discountId} className="discount-item">
                      <div className="discount-info">
                        <div className="discount-badge">{discount.discount}%</div>
                        <span className="discount-product">
                          {!discount.product ? 'All Products' : discount.product}
                        </span>
                      </div>
                      <button 
                        className={`delete-button ${deleteStatus?.id === discount.discountId ? deleteStatus.status : ''}`}
                        onClick={() => handleDeleteDiscount(discount.discountId)}
                        disabled={deleteStatus?.id === discount.discountId && deleteStatus.status === 'deleting'}
                        aria-label="Delete discount"
                        title="Delete this discount"
                      >
                        {deleteStatus?.id === discount.discountId && deleteStatus.status === 'deleting' 
                          ? <span className="deleting-text">Deleting...</span>
                          : <span className="delete-icon">🗑️</span>}
                      </button>
                    </div>
                  );
                })}
              </div>
              
              <div className="discount-count">
                {user.discounts.length} {user.discounts.length === 1 ? 'discount' : 'discounts'}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ViewDiscounts;